import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiPost } from '../lib/apiClient';
import { openCustomerPortal } from '../utils/cn';

const MAPBOX_TOKEN = import.meta.env?.VITE_MAPBOX_ACCESS_TOKEN;

// City data with coordinates for each tier
const ACTIVE_CITIES = [
  { city: 'Dayton', state: 'OH', lat: 39.7589, lng: -84.1916 },
  { city: 'Columbus', state: 'OH', lat: 39.9612, lng: -82.9988 },
  { city: 'Cincinnati', state: 'OH', lat: 39.1031, lng: -84.5120 },
];

const COMING_SOON_CITIES = [
  { city: 'Dallas', state: 'TX', lat: 32.7767, lng: -96.7970 },
  { city: 'Houston', state: 'TX', lat: 29.7604, lng: -95.3698 },
  { city: 'San Antonio', state: 'TX', lat: 29.4241, lng: -98.4936 },
  { city: 'Austin', state: 'TX', lat: 30.2672, lng: -97.7431 },
];

const NEXT_12_CITIES = [
  { city: 'Louisville', state: 'KY', lat: 38.2527, lng: -85.7585 },
  { city: 'Lexington', state: 'KY', lat: 38.0406, lng: -84.5037 },
  { city: 'Pittsburgh', state: 'PA', lat: 40.4406, lng: -79.9959 },
  { city: 'Indianapolis', state: 'IN', lat: 39.7684, lng: -86.1581 },
  { city: 'Kansas City', state: 'MO', lat: 39.0997, lng: -94.5786 },
  { city: 'Denver', state: 'CO', lat: 39.7392, lng: -104.9903 },
  { city: 'Colorado Springs', state: 'CO', lat: 38.8339, lng: -104.8214 },
  { city: 'Omaha', state: 'NE', lat: 41.2565, lng: -95.9345 },
  { city: 'Washington', state: 'DC', lat: 38.9072, lng: -77.0369 },
  { city: 'Virginia Beach', state: 'VA', lat: 36.8529, lng: -75.9780 },
  { city: 'Charleston', state: 'SC', lat: 32.7765, lng: -79.9311 },
  { city: 'Philadelphia', state: 'PA', lat: 39.9526, lng: -75.1652 },
];

// Haversine distance formula (returns miles)
function haversineDistance(lat1, lng1, lat2, lng2) {
  const R = 3958.8;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function findTier(lat, lng) {
  let closest = null;
  let closestDist = Infinity;
  for (const city of ACTIVE_CITIES) {
    const dist = haversineDistance(lat, lng, city?.lat, city?.lng);
    if (dist < closestDist) { closestDist = dist; closest = { ...city, dist }; }
    if (dist <= 60) return { tier: 'active', city, dist };
  }
  for (const city of COMING_SOON_CITIES) {
    const dist = haversineDistance(lat, lng, city?.lat, city?.lng);
    if (dist < closestDist) { closestDist = dist; closest = { ...city, dist }; }
    if (dist <= 60) return { tier: 'coming_soon', city, dist };
  }
  for (const city of NEXT_12_CITIES) {
    const dist = haversineDistance(lat, lng, city?.lat, city?.lng);
    if (dist < closestDist) { closestDist = dist; closest = { ...city, dist }; }
    if (dist <= 75) return { tier: 'next_12', city, dist };
  }
  return { tier: 'waitlist', city: closest, dist: closestDist };
}

const ActiveResult = ({ city, address }) => {
  const handleEstimate = () => {
    // The visitor already typed/geocoded an in-coverage address here — carry it
    // into the portal so the wizard opens pre-filled.
    openCustomerPortal({
      address,
      city: city?.city,
      state: city?.state,
    });
  };

  return (
    <div
      className="mt-6 rounded-2xl p-6 border"
      style={{ backgroundColor: '#F0FDF4', borderColor: '#86EFAC', boxShadow: '0 4px 24px rgba(34,197,94,0.10)' }}
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#22C55E' }}>
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full" style={{ backgroundColor: '#22C55E', color: '#fff' }}>Active</span>
          </div>
          <h3 className="text-xl font-black mb-1" style={{ color: '#000150', fontFamily: "'Poppins', 'Inter', sans-serif" }}>
            Great news! We service {city?.city}, {city?.state}
          </h3>
          <p className="text-gray-600 text-sm mb-4">OpenbooQ is actively replacing roofs in your area. Get your roof price now.</p>
          <button
            onClick={handleEstimate}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-bold text-sm transition-all duration-300 hover:opacity-90 hover:shadow-lg hover:scale-105"
            style={{ backgroundColor: '#07B0E9', boxShadow: '0 4px 16px rgba(7,176,233,0.35)' }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Get Roof Price
          </button>
        </div>
      </div>
    </div>
  );
};

const EmailCaptureResult = ({ city, tier, lookupId }) => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const config = {
    coming_soon: {
      badgeColor: '#033494', badgeText: 'Coming Soon', bg: '#EFF6FF', border: '#BFDBFE',
      shadow: 'rgba(3,52,148,0.08)', headline: `We're coming to ${city?.city} soon!`,
      sub: `OpenbooQ is launching in ${city?.city}, ${city?.state} soon. Be the first to know when we go live.`,
      btnColor: '#033494', btnText: 'Notify Me', successColor: '#033494',
    },
    next_12: {
      badgeColor: '#F59E0B', badgeText: 'Next 12 Months', bg: '#FFFBEB', border: '#FDE68A',
      shadow: 'rgba(245,158,11,0.08)', headline: `We're planning ${city?.city} in the next 12 months!`,
      sub: `OpenbooQ is coming to ${city?.city}, ${city?.state} within the year. Join the waitlist to be first.`,
      btnColor: '#F59E0B', btnText: 'Join Waitlist', successColor: '#D97706',
    },
    waitlist: {
      badgeColor: '#6B7280', badgeText: 'National Waitlist', bg: '#F9FAFB', border: '#E5E7EB',
      shadow: 'rgba(107,114,128,0.08)', headline: "We haven\'t reached your area yet",
      sub: "OpenbooQ is expanding nationwide. Join our national waitlist and we'll notify you when we launch near you.",
      btnColor: '#6B7280', btnText: 'Join National Waitlist', successColor: '#374151',
    },
  };

  const c = config?.[tier];

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!email?.trim()) return;
    setSubmitting(true);
    try {
      // Launch / coming-soon signup -> backend (public, rate-limited).
      // Carries the lookup id so the backend can attach the email to the
      // existing address_lookups row. Fallback-first: silent fail, still
      // show success to the user (never blocks the signup UX).
      await apiPost('/launch-notify', {
        email: email?.trim(),
        lookupId: lookupId || null,
        tier,
        city: city?.city || null,
        state: city?.state || null,
      });
    } catch {
      // Silent fail — still show success to user
    } finally {
      setSubmitting(false);
      setSubmitted(true);
    }
  };

  return (
    <div
      className="mt-6 rounded-2xl p-6 border"
      style={{ backgroundColor: c?.bg, borderColor: c?.border, boxShadow: `0 4px 24px ${c?.shadow}` }}
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: c?.badgeColor }}>
          {tier === 'waitlist' ? (
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          ) : (
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full text-white" style={{ backgroundColor: c?.badgeColor }}>{c?.badgeText}</span>
          </div>
          <h3 className="text-xl font-black mb-1" style={{ color: '#000150', fontFamily: "'Poppins', 'Inter', sans-serif" }}>{c?.headline}</h3>
          <p className="text-gray-600 text-sm mb-4">{c?.sub}</p>
          {submitted ? (
            <div className="flex items-center gap-2 text-sm font-semibold py-2" style={{ color: c?.successColor }}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
              You're on the list! We'll be in touch.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                placeholder="Enter your email address"
                required
                className="flex-1 px-4 py-2.5 text-sm border rounded-full outline-none bg-white transition-colors duration-200"
                style={{ borderColor: c?.border, fontFamily: "'Inter', sans-serif" }}
              />
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2.5 text-sm font-bold rounded-full text-white transition-all duration-300 hover:opacity-90 hover:shadow-md whitespace-nowrap disabled:opacity-60"
                style={{ backgroundColor: c?.btnColor }}
              >
                {submitting ? 'Saving...' : c?.btnText}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const AddressChecker = ({ className = '', inputClassName = '' }) => {
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);

  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef?.current && !containerRef?.current?.contains(e?.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query?.trim()?.length < 3) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    setSuggestionsLoading(true);
    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query?.trim())}.json?access_token=${MAPBOX_TOKEN}&autoComplete=true&types=address&country=US&limit=5`;
      const res = await fetch(url);
      const data = await res?.json();
      if (data?.features && data?.features?.length > 0) {
        setSuggestions(data?.features);
        setShowDropdown(true);
        setActiveSuggestion(-1);
      } else {
        setSuggestions([]);
        setShowDropdown(false);
      }
    } catch {
      setSuggestions([]);
      setShowDropdown(false);
    } finally {
      setSuggestionsLoading(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const val = e?.target?.value;
    setAddress(val);
    setResult(null);
    setError(null);

    if (debounceRef?.current) clearTimeout(debounceRef?.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(val);
    }, 300);
  };

  const insertLookupRecord = async (addressText, lat, lon, tierResult) => {
    try {
      const tierLabel = {
        active: 'Active',
        coming_soon: 'Coming Soon',
        next_12: 'Next 12 Months',
        waitlist: 'Waitlist',
      }?.[tierResult?.tier] || tierResult?.tier;

      const nearestCity = tierResult?.city
        ? `${tierResult?.city?.city}, ${tierResult?.city?.state}`
        : null;

      // Address checker insert -> backend (public, rate-limited). Returns the
      // created row; we only need its id to attach a later launch signup.
      // Fallback-first: any failure returns null (lookupId stays null, the
      // coverage result still renders).
      const created = await apiPost('/address-lookups', {
        address: addressText,
        latitude: lat,
        longitude: lon,
        tier: tierLabel,
        nearestCity,
      });

      return created?.id || null;
    } catch {
      return null;
    }
  };

  // Handle Mapbox feature selection (feature.center = [lng, lat])
  const handleSelectSuggestion = async (feature) => {
    const displayName = feature?.place_name || '';
    const lng = feature?.center?.[0];
    const lat = feature?.center?.[1];

    setAddress(displayName);
    setSuggestions([]);
    setShowDropdown(false);
    setActiveSuggestion(-1);

    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const tierResult = findTier(lat, lng);
      const lookupId = await insertLookupRecord(displayName, lat, lng, tierResult);
      setResult({ ...tierResult, lookupId });
    } catch {
      setError('Unable to check coverage. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions?.length === 0) return;
    if (e?.key === 'ArrowDown') {
      e?.preventDefault();
      setActiveSuggestion((prev) => Math.min(prev + 1, suggestions?.length - 1));
    } else if (e?.key === 'ArrowUp') {
      e?.preventDefault();
      setActiveSuggestion((prev) => Math.max(prev - 1, 0));
    } else if (e?.key === 'Enter' && activeSuggestion >= 0) {
      e?.preventDefault();
      handleSelectSuggestion(suggestions?.[activeSuggestion]);
    } else if (e?.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  // Manual submit: geocode typed address via Mapbox
  const handleCheck = async (e) => {
    e?.preventDefault();
    if (!address?.trim()) return;
    setShowDropdown(false);
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address?.trim())}.json?access_token=${MAPBOX_TOKEN}&types=address&country=US&limit=1`;
      const res = await fetch(url);
      const data = await res?.json();
      if (!data?.features || data?.features?.length === 0) {
        setError('Address not found. Please try a more specific address.');
        setLoading(false);
        return;
      }
      const feature = data?.features?.[0];
      const lng = feature?.center?.[0];
      const lat = feature?.center?.[1];
      const tierResult = findTier(lat, lng);
      const lookupId = await insertLookupRecord(address?.trim(), lat, lng, tierResult);
      setResult({ ...tierResult, lookupId });
    } catch {
      setError('Unable to look up address. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={className}>
      <form onSubmit={handleCheck} className="flex flex-col sm:flex-row gap-3">
        <div ref={containerRef} className="flex-1 relative">
          <input
            ref={inputRef}
            type="text"
            value={address}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => suggestions?.length > 0 && setShowDropdown(true)}
            placeholder="Enter your home address..."
            className={`w-full px-6 py-4 rounded-full text-gray-900 text-sm font-medium outline-none focus:ring-2 focus:ring-blue-400 bg-white ${inputClassName}`}
            style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.10)' }}
            disabled={loading}
            autoComplete="off"
          />
          {/* Mapbox suggestions dropdown */}
          {showDropdown && suggestions?.length > 0 && (
            <ul
              className="absolute left-0 right-0 top-full mt-2 bg-white rounded-2xl border border-gray-100 overflow-hidden z-50"
              style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.12)', maxHeight: '260px', overflowY: 'auto' }}
            >
              {suggestions?.map((feature, i) => (
                <li
                  key={feature?.id || i}
                  onMouseDown={(e) => { e?.preventDefault(); handleSelectSuggestion(feature); }}
                  className={`flex items-start gap-3 px-5 py-3 cursor-pointer transition-colors duration-150 ${
                    i === activeSuggestion ? 'bg-blue-50' : 'hover:bg-gray-50'
                  } ${i < suggestions?.length - 1 ? 'border-b border-gray-100' : ''}`}
                >
                  <svg
                    className="w-4 h-4 mt-0.5 flex-shrink-0"
                    style={{ color: '#07B0E9' }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-sm text-gray-700 leading-snug" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {feature?.place_name}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {/* Loading indicator */}
          {suggestionsLoading && (
            <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none">
              <svg className="w-4 h-4 animate-spin" style={{ color: '#07B0E9' }} fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
          )}
        </div>
        <button
          type="submit"
          disabled={loading || !address?.trim()}
          className="px-8 py-4 rounded-full text-white font-bold text-sm whitespace-nowrap transition-all duration-300 hover:opacity-90 hover:shadow-xl hover:scale-105 disabled:opacity-60 disabled:cursor-not-allowed disabled:scale-100"
          style={{ backgroundColor: '#07B0E9', boxShadow: '0 4px 20px rgba(7,176,233,0.4)' }}
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Checking...
            </span>
          ) : (
            'Check Coverage'
          )}
        </button>
      </form>
      {/* Error state */}
      {error && (
        <div
          className="mt-4 flex items-center gap-3 px-5 py-4 rounded-2xl border"
          style={{ backgroundColor: '#FEF2F2', borderColor: '#FECACA' }}
        >
          <svg className="w-5 h-5 flex-shrink-0" style={{ color: '#EF4444' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm font-medium" style={{ color: '#B91C1C' }}>{error}</p>
        </div>
      )}
      {/* Result card */}
      {result && (
        <>
          {result?.tier === 'active' && <ActiveResult city={result?.city} address={address} />}
          {result?.tier === 'coming_soon' && <EmailCaptureResult city={result?.city} tier="coming_soon" lookupId={result?.lookupId} />}
          {result?.tier === 'next_12' && <EmailCaptureResult city={result?.city} tier="next_12" lookupId={result?.lookupId} />}
          {result?.tier === 'waitlist' && <EmailCaptureResult city={result?.city} tier="waitlist" lookupId={result?.lookupId} />}
        </>
      )}
    </div>
  );
};

export default AddressChecker;
